import { FileWithShare } from "@/lib/supabase/supabaseTypes";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from "@/dentlab/src/components/ui/hover-card";
import { ShareFileButton } from "@/dentlab/src/components/sharing/share-button/share-file-button.component";
import clsx from "clsx";
import { FileIconComponent } from "../file-icon/file-icon.component";
import { getFileTypeWithDocumentType } from "@/lib/utils/file-utils";
import { getFileTooltip } from "./file-utils";
import { UnshareDialog } from "../sharing/unshare-dialog/unshare-dialog.component";
import { JobDocumentWithFiles } from "../../store/Jobs/job-documents.store";
import { InvoiceCancellationDialog } from "../../pages/debitoren/components/invoice-cancellation-dialog.component";
import { useFileActions } from "../../hooks/useFileActions";
import { useStorage } from "../../hooks/useStorage";
import { dispatchPrint } from "../../lib/utils/print-dispatch";
import { showNotification } from "../../store/Central/selectors";
import { PdfTemplateType } from "../../pdf-templates/document-template.types";

export const FileDropDown = ({
	file,
	jobDocument,
	options,
	onOpenFile,
	side = "bottom",
}: {
	file: FileWithShare;
	jobDocument?: JobDocumentWithFiles;
	options?: {
		blockShareButtons?: boolean;
		blockDelete?: boolean;
	};
	onOpenFile?: (fileId: string) => void;
	side?: "bottom" | "top" | "left" | "right";
}) => {
	const {
		openShareDialog,
		setOpenShareDialog,
		openUnshareDialog,
		setOpenUnshareDialog,
		openCancelInvoiceDialog,
		cancellationLoading,
		invoiceData,
		handleShare,
		handleUnshare,
		handleDelete,
		handleDownload,
		handleOpenFileInNewTab,
		fetchInvoicePaymentData,
		handleCancelInvoice,
		handleCancelInvoiceDialogClose,
		canShare,
		canDelete,
		canCancelInvoice,
		isFileShared,
		wasFileShared,
		shareColor,
	} = useFileActions({ file, jobDocument, options });

	const { fetchFileFromPath } = useStorage();

	const fileType = getFileTypeWithDocumentType(
		(file?.meta_data || null) as Record<string, any> | null,
		file.file_name
	);

	const isPdf =
		file.file_name?.toLowerCase().endsWith(".pdf") ||
		fileType === "pdf" ||
		fileType === PdfTemplateType.LIEFERSCHEIN ||
		fileType === PdfTemplateType.KOSTENVORANSCHLAG ||
		fileType === PdfTemplateType.GUTSCHRIFT;

	const handlePrint = async () => {
		if (!file.path_name) return;
		const { success, data: signedUrl } = await fetchFileFromPath(
			file.path_name
		);
		if (!success || !signedUrl) {
			showNotification({
				message: "Fehler beim Laden der Datei",
				type: "error",
			});
			return;
		}
		const response = await fetch(signedUrl);
		if (!response.ok) {
			showNotification({
				message: "Fehler beim Laden der Datei",
				type: "error",
			});
			return;
		}
		const blob = await response.blob();
		const metaData = file.meta_data as Record<string, any> | null;
		const documentType = metaData?.pdf_template_type ?? "default";
		await dispatchPrint({
			documentType,
			documentData: blob,
			fileName: file.file_name ?? "Dokument.pdf",
		});
	};

	return (
		<>
			<div className="flex gap-1 items-center hover:bg-neutral-100 px-2 rounded-sm">
				<FileIconComponent fileType={fileType} color={shareColor} />

				<HoverCard openDelay={200} closeDelay={100}>
					<HoverCardTrigger asChild>
						<div
							className={clsx(
								"max-w-48 cursor-pointer text-ellipsis overflow-hidden whitespace-nowrap text-xs",
								file.invoice_canceled_at &&
									"line-through opacity-60 text-red-600"
							)}
							title={getFileTooltip(file)}
							onClick={() => {
								if (onOpenFile && file.id) {
									onOpenFile(file.id);
								} else {
									handleOpenFileInNewTab();
								}
							}}
						>
							{file.file_name}
						</div>
					</HoverCardTrigger>
					<HoverCardContent
						className="w-auto p-1"
						sideOffset={5}
						side={side}
					>
						<div className="flex flex-col">
							<button
								onClick={(e) => {
									e.stopPropagation();
									handleDownload();
								}}
								className="flex justify-between items-center cursor-pointer hover:bg-neutral-100 px-3 py-1.5 text-sm rounded-sm text-left"
							>
								Herunterladen
							</button>
							{isPdf && (
								<button
									onClick={(e) => {
										e.stopPropagation();
										handlePrint();
									}}
									className="flex justify-between items-center cursor-pointer hover:bg-neutral-100 px-3 py-1.5 text-sm rounded-sm text-left"
									data-testid="print-file-button"
									aria-label="Datei drucken"
								>
									Drucken
								</button>
							)}
							{canShare && (
								<button
									onClick={(e) => {
										e.stopPropagation();
										if (isFileShared) {
											setOpenUnshareDialog(true);
										} else {
											setOpenShareDialog(true);
										}
									}}
									className="flex justify-between items-center cursor-pointer hover:bg-neutral-100 px-3 py-1.5 text-sm rounded-sm text-left"
								>
									{isFileShared
										? "Teilen aufheben"
										: wasFileShared
											? "Erneut Teilen"
											: "Teilen"}
								</button>
							)}
							{canCancelInvoice && (
								<button
									onClick={(e) => {
										e.stopPropagation();
										fetchInvoicePaymentData();
									}}
									className="flex justify-between items-center cursor-pointer hover:bg-neutral-100 px-3 py-1.5 text-sm rounded-sm text-left"
									data-testid="cancel-invoice-button"
								>
									Stornieren
								</button>
							)}
							{canDelete && (
								<button
									onClick={(e) => {
										e.stopPropagation();
										handleDelete();
									}}
									className="flex justify-between items-center cursor-pointer hover:bg-neutral-100 px-3 py-1.5 text-sm rounded-sm text-left"
								>
									In den Papierkorb legen
								</button>
							)}
						</div>
					</HoverCardContent>
				</HoverCard>
			</div>
			{openShareDialog && (
				<ShareFileButton
					open={openShareDialog}
					setOpen={setOpenShareDialog}
					file={file}
					handleShare={handleShare}
				/>
			)}
			{openUnshareDialog && (
				<UnshareDialog
					open={openUnshareDialog}
					onClose={() => setOpenUnshareDialog(false)}
					shared={isFileShared}
					entityName={file.file_name}
					onUnshare={handleUnshare}
				/>
			)}
			{openCancelInvoiceDialog && (
				<InvoiceCancellationDialog
					isOpen={openCancelInvoiceDialog}
					onClose={handleCancelInvoiceDialogClose}
					onConfirm={handleCancelInvoice}
					loading={cancellationLoading}
					invoice={invoiceData}
				/>
			)}
		</>
	);
};
